import {udApi} from '@udemy/ud-api';

import type {SurveyAnswerOption} from '../types';

export interface SurveyStructure {
    surveyId: number;
    choiceQuestionId: number | null;
    freeformQuestionId: number | null;
    answerOptions: SurveyAnswerOption[];
    optionIdByCode: Record<string, number>;
}

// Raw monolith REST shape — kept snake_case and Api-prefixed to distinguish it from
// SurveyAnswerOption, the camelCase shape used internally once mapped below.
interface ApiSurveyAnswerOption {
    id: number;
    order: number;
    text_code: string;
    text: string;
}

export async function fetchAtRiskSurvey(surveyCode: string): Promise<SurveyStructure> {
    const response = await udApi.get(`/surveys/${surveyCode}/`);
    const data = response.data;
    const questions: Array<{
        id: number;
        question_type: string;
        answer_options: ApiSurveyAnswerOption[];
    }> = data.question_sets?.[0]?.questions ?? [];

    const choiceQuestion = questions.find((q) => q.question_type === 'choice');
    const freeformQuestion = questions.find((q) => q.question_type === 'freeform');
    const answerOptions: SurveyAnswerOption[] = (choiceQuestion?.answer_options ?? []).map(
        (opt) => ({
            id: opt.id,
            order: opt.order,
            textCode: opt.text_code,
            text: opt.text,
        }),
    );
    const optionIdByCode: Record<string, number> = {};
    for (const opt of answerOptions) {
        optionIdByCode[opt.textCode] = opt.id;
    }

    return {
        surveyId: data.id,
        choiceQuestionId: choiceQuestion?.id ?? null,
        freeformQuestionId: freeformQuestion?.id ?? null,
        answerOptions,
        optionIdByCode,
    };
}

interface SubmitAtRiskSurveyAnswerParams {
    surveyId: number;
    choiceQuestionId: number;
    optionId: number | null;
    freeformQuestionId?: number | null;
    freeformText?: string;
}

export async function submitAtRiskSurveyAnswer({
    surveyId,
    choiceQuestionId,
    optionId,
    freeformQuestionId,
    freeformText,
}: SubmitAtRiskSurveyAnswerParams): Promise<void> {
    const payload: Array<{
        question_id: number;
        answer_option_id: number | null;
        answer_freeform?: string;
    }> = [
        {
            question_id: choiceQuestionId,
            answer_option_id: optionId,
        },
    ];

    if (freeformText && freeformQuestionId) {
        payload.push({
            question_id: freeformQuestionId,
            answer_option_id: null,
            answer_freeform: freeformText,
        });
    }

    await udApi.put(`/users/me/surveys/${surveyId}/answers/?no_overwrite=1`, payload);
}
